<template>
  <div>
    <head>
      <meta charset="utf-8"/>
      <meta content="width=device-width, initial-scale=1.0" name="viewport">
      <meta content="Rental management system by Nouveta LTD" name="description"/>
      <meta content="Kelvin Njuguna" name="author"/>
      <!-- App favicon -->
      <link href="assets/images/favicon.png" rel="shortcut icon">
      <!-- DataTables -->
      <link href="assets/libs/datatables.net-bs4/css/dataTables.bootstrap4.min.css" rel="stylesheet" type="text/css"/>
      <link href="assets/libs/datatables.net-buttons-bs4/css/buttons.bootstrap4.min.css" rel="stylesheet" type="text/css"/>
      <!-- Bootstrap Css -->
      <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css"/>
      <!-- Bootstrap select styling -->
      <link href="https://cdn.jsdelivr.net/npm/bootstrap-select@1.13.14/dist/css/bootstrap-select.min.css" rel="stylesheet">
      <!-- Icons Css -->
      <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css"/>
      <!-- App Css-->
      <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css"/>
      <!-- custom Css-->
      <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css"/>
    </head>

    <body data-sidebar="dark">
    <!-- Begin page -->
    <div id="layout-wrapper">
      <NavigationBar/>

      <!-- Start right Content here -->
      <div id="content" class="content-minimize">
        <div class="page-content">
          <div class="container-fluid">

            <!-- Page title -->
            <div class="row">
              <div class="col-12">
                <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 class="mb-sm-0 font-size-18">DATA CLEANUP</h4>
                  <div class="page-title-right">
                    <ol class="breadcrumb m-0">
                      <li class="breadcrumb-item"><a @click="gotTo('dashboard')">Dashboards</a></li>
                      <li class="breadcrumb-item active">Data Cleanup</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            <!-- Summary Card -->
            <div class="row mb-4">
              <div class="col-12">
                <div class="card bg-warning text-dark">
                  <div class="card-body">
                    <div class="row align-items-center">
                      <div class="col-4">
                        <div class="d-flex align-items-center mb-2">
                          <h5 class="text-dark mb-0 me-2">System Total:</h5>
                          <div v-if="loadingSystemCount" class="spinner-border spinner-border-sm text-dark" role="status">
                            <span class="visually-hidden">Loading...</span>
                          </div>
                          <h3 v-else class="text-dark mb-0">{{ numberFormat(systemOrphanedCount) }}</h3>
                        </div>
                        <small class="text-muted">Total orphaned records in entire system</small>
                      </div>
                      <div class="col-4">
                        <h5 class="text-dark mb-1">Filtered Results:</h5>
                        <h2 class="text-dark mb-0">{{ numberFormat(summary.count || 0) }}</h2>
                        <small class="text-muted">PAID bills without transactions (current filter)</small>
                      </div>
                      <div class="col-2 text-center">
                        <div class="badge bg-dark text-white fs-6 px-3 py-2 mb-2">
                          {{ selectedRecords.length }} Selected
                        </div>
                        <div class="text-muted small">
                          Page {{ currentPage }} of {{ totalPages }}
                        </div>
                      </div>
                      <div class="col-2 text-end">
                        <div class="text-warning">
                          <i class="mdi mdi-alert-circle font-size-24"></i>
                        </div>
                        <small class="text-muted d-block">Data cleanup required</small>
                        <button @click="getSystemOrphanedCount()"
                                class="btn btn-outline-dark btn-sm mt-2"
                                :disabled="loadingSystemCount">
                          <i class="mdi mdi-refresh"></i>
                          <span v-if="loadingSystemCount">...</span>
                          <span v-else>Refresh Count</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Progress Card (Show during deletion) -->
            <div class="row mb-4" v-if="isDeleting">
              <div class="col-12">
                <div class="card bg-info text-white">
                  <div class="card-body">
                    <div class="row align-items-center">
                      <div class="col-8">
                        <h5 class="text-white mb-1">Deletion in Progress</h5>
                        <div class="progress bg-white bg-opacity-25 mb-2">
                          <div class="progress-bar bg-white"
                               :style="{width: deletionProgress + '%'}"
                               :aria-valuenow="deletionProgress"
                               aria-valuemin="0"
                               aria-valuemax="100">
                            {{ Math.round(deletionProgress) }}%
                          </div>
                        </div>
                        <small>{{ deletionStatus }}</small>
                      </div>
                      <div class="col-4 text-end">
                        <h3 class="text-white mb-0">{{ deletedCount }}/{{ totalToDelete }}</h3>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Main content -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <!-- Header -->
                  <div class="card-header bg-white border-bottom">
                    <div class="d-flex justify-content-between align-items-center">
                      <h4 class="card-title mb-0">Orphaned Bill Records</h4>
                      <div>
                        <button v-if="selectedRecords.length > 0"
                                @click="deleteSelected()"
                                class="btn btn-danger me-2"
                                :disabled="isDeleting"
                                type="button">
                          <i class="mdi mdi-delete font-size-16"></i>
                          <span class="d-md-inline ms-1">Delete Selected ({{ selectedRecords.length }})</span>
                        </button>
                        <button @click="refreshAllData()"
                                class="btn btn-primary"
                                :disabled="loading || isDeleting || loadingSystemCount"
                                type="button">
                          <i class="mdi mdi-refresh font-size-16"></i>
                          <span class="d-md-inline ms-1">Refresh All</span>
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Filters -->
                  <div class="card-header bg-white border-bottom">
                    <div class="row g-3 align-items-center">
                      <!-- Rows per page -->
                      <div class="col-md-2">
                        <label class="form-label mb-1">Rows Per Page:</label>
                        <select v-model="pageSize" @change="handlePageSizeChange()" class="form-select">
                          <option value="10">10 Rows</option>
                          <option value="25">25 Rows</option>
                          <option value="50">50 Rows</option>
                          <option value="100">100 Rows</option>
                          <option value="200">200 Rows</option>
                          <option value="500">500 Rows</option>
                          <option value="1000">1000 Rows</option>
                          <option value="2000">2000 Rows</option>
                          <option value="3000">3000 Rows</option>
                          <option value="4000">4000 Rows</option>
                          <option value="5000">5000 Rows</option>
                        </select>
                      </div>

                      <!-- Date From -->
                      <div class="col-md-2">
                        <label class="form-label mb-1">From:</label>
                        <input v-model="dateFrom" class="form-control" type="date" @change="handleDateChange">
                      </div>

                      <!-- Date To -->
                      <div class="col-md-2">
                        <label class="form-label mb-1">To:</label>
                        <input v-model="dateTo" class="form-control" type="date" @change="handleDateChange">
                      </div>

                      <!-- Search -->
                      <div class="col-md-6">
                        <label class="form-label mb-1">Search:</label>
                        <div class="position-relative">
                          <input v-model="search" type="text" class="form-control"
                                 placeholder="Search by Bill Number, Customer Name, etc."
                                 @input="debounceSearch">
                          <i class="bx bx-search-alt position-absolute top-50 end-0 translate-middle-y me-3"></i>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Bulk Actions -->
                  <div class="card-header bg-white border-bottom" v-if="billDetails.length > 0">
                    <div class="d-flex justify-content-between align-items-center">
                      <div class="d-flex align-items-center">
                        <div class="form-check me-3">
                          <input class="form-check-input"
                                 type="checkbox"
                                 id="selectAll"
                                 :checked="isAllSelected"
                                 @change="toggleSelectAll">
                          <label class="form-check-label" for="selectAll">
                            Select All ({{ billDetails.length }})
                          </label>
                        </div>
                        <span class="text-muted">{{ selectedRecords.length }} of {{ billDetails.length }} selected</span>
                      </div>
                      <div>
                        <button v-if="selectedRecords.length > 0"
                                @click="clearSelection()"
                                class="btn btn-outline-secondary btn-sm me-2">
                          Clear Selection
                        </button>
                        <span class="badge bg-secondary">Page {{ currentPage }} of {{ totalPages }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Table -->
                  <div class="card-body p-0">
                    <div class="table-responsive">
                      <table class="table table-hover mb-0">
                        <thead class="table-dark">
                        <tr>
                          <th width="50">
                            <div class="form-check">
                              <input class="form-check-input"
                                     type="checkbox"
                                     :checked="isAllSelected"
                                     @change="toggleSelectAll">
                            </div>
                          </th>
                          <th class="text-nowrap">NO.</th>
                          <th class="text-nowrap">Bill No</th>
                          <th class="text-nowrap">Description</th>
                          <th class="text-nowrap">Customer</th>
                          <th class="text-nowrap">Location</th>
                          <th class="text-nowrap">Amount</th>
                          <th class="text-nowrap">Paid</th>
                          <th class="text-nowrap">Balance</th>
                          <th class="text-nowrap">Date</th>
                          <th class="text-nowrap">Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(item, index) in billDetails" :key="item.billNo || index">
                          <td>
                            <div class="form-check">
                              <input class="form-check-input"
                                     type="checkbox"
                                     :value="item.billNo"
                                     v-model="selectedRecords">
                            </div>
                          </td>
                          <td class="text-nowrap">{{ ((currentPage - 1) * actualPageSize) + index + 1 }}</td>
                          <td class="text-nowrap">
                            <span class="fw-semibold text-primary">{{ item.billNo || 'N/A' }}</span>
                          </td>
                          <td style="min-width: 200px;">
                            <div>
                              <strong>{{ item.incomeTypeDescription || 'N/A' }}</strong>
                              <small class="text-muted d-block">{{ item.feeDescription || '' }}</small>
                            </div>
                          </td>
                          <td>
                            <div>
                              <strong>{{ item.names || 'Unknown Customer' }}</strong>
                              <small class="text-muted d-block">ID: {{ maskPhoneNumber(item.idNo) || 'N/A' }}</small>
                              <small class="text-muted d-block">{{ item.paidBy ? 'Paid by: ' + item.paidBy : '' }}</small>
                            </div>
                          </td>
                          <td class="text-nowrap">
                            <div>
                              <div>{{ item.subCountyName || 'N/A' }}</div>
                              <small class="text-muted">{{ item.wardName || '' }}</small>
                              <small class="text-muted d-block">{{ item.zone || '' }}</small>
                            </div>
                          </td>
                          <td class="text-nowrap text-end">
                            <span class="fw-semibold">{{ formatPrice(item.detailAmount || 0) }}</span>
                          </td>
                          <td class="text-nowrap text-end">
                            <span class="fw-semibold text-success">{{ formatPrice(item.receiptAmount || 0) }}</span>
                          </td>
                          <td class="text-nowrap text-end">
                            <span class="fw-semibold" :class="{'text-danger': parseFloat(item.billBalance || 0) > 0, 'text-success': parseFloat(item.billBalance || 0) < 0}">
                              {{ formatPrice(item.billBalance || 0) }}
                            </span>
                          </td>
                          <td class="text-nowrap">{{ formatDate(item.dateCreated) }}</td>
                          <td class="text-nowrap">
                            <button @click="deleteSingleRecord(item.billNo)"
                                    class="btn btn-danger btn-sm"
                                    :disabled="isDeleting"
                                    title="Delete this orphaned record">
                              <i class="mdi mdi-delete"></i>
                              Delete
                            </button>
                          </td>
                        </tr>
                        </tbody>
                      </table>

                      <!-- Loading State -->
                      <div v-if="loading" class="text-center p-5">
                        <div class="spinner-border text-info mb-3" role="status">
                          <span class="visually-hidden">Loading...</span>
                        </div>
                        <h4>Loading Orphaned Records</h4>
                        <p>Searching for bills without corresponding transactions...</p>
                      </div>

                      <!-- No Results -->
                      <div v-if="billDetails.length < 1 && !loading" class="text-center p-5">
                        <img :src="'./assets/images/no-results.png'" class="img-fluid mb-4" alt="No results" style="max-width: 200px;">
                        <h4>No Orphaned Records Found</h4>
                        <p>{{ emptyStateMessage }}</p>
                      </div>
                    </div>
                  </div>

                  <!-- Pagination -->
                  <div class="card-footer bg-white" v-if="billDetails.length > 0">
                    <nav class="d-flex align-items-center justify-content-between">
                      <div class="d-flex align-items-center">
                        <span class="me-3">Rows Per Page</span>
                        <select class="form-select w-auto" v-model="pageSize" @change="handlePageSizeChange">
                          <option value="10">10 Rows</option>
                          <option value="25">25 Rows</option>
                          <option value="50">50 Rows</option>
                          <option value="100">100 Rows</option>
                          <option value="200">200 Rows</option>
                          <option value="500">500 Rows</option>
                          <option value="1000">1000 Rows</option>
                          <option value="2000">2000 Rows</option>
                          <option value="3000">3000 Rows</option>
                          <option value="4000">4000 Rows</option>
                          <option value="5000">5000 Rows</option>
                        </select>
                        <span class="ms-3 text-muted">
                          Showing {{ paginationStart }} to {{ paginationEnd }} of {{ summary.count }} entries
                        </span>
                      </div>

                      <ul class="pagination mb-0">
                        <li class="page-item" :class="{ disabled: currentPage === 1 }">
                          <button class="page-link" @click="prevPage" :disabled="currentPage === 1">Previous</button>
                        </li>
                        <li v-for="page in visiblePages" :key="'page-' + page"
                            class="page-item" :class="{ active: currentPage === page }">
                          <span v-if="page === '...'" class="page-link">{{ page }}</span>
                          <button v-else class="page-link" @click="goToPage(page)">{{ page }}</button>
                        </li>
                        <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                          <button class="page-link" @click="nextPage" :disabled="currentPage === totalPages">Next</button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Alert for actions -->
        <div v-if="alert.success || alert.failed || alert.info" class="toast-container position-fixed top-0 end-0 p-3">
          <div class="toast show" role="alert">
            <div class="toast-header" :class="{
                'bg-success text-white': alert.success,
                'bg-danger text-white': alert.failed,
                'bg-info text-white': alert.info
              }">
              <strong class="me-auto">Notification</strong>
              <button type="button" class="btn-close" @click="clearAlerts"></button>
            </div>
            <div class="toast-body">
              {{ message }}
            </div>
          </div>
        </div>

        <footer class="footer">
          <div class="container-fluid">
            <div class="row">
              <div class="col-sm-6"></div>
              <div class="col-sm-6">
                <div class="text-sm-end">Developed by County</div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
    </body>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, biller, execute, getRight } from "@/api";
import NavigationBar from "@/components/Navigation.vue";
import router from "@/router";
import moment from "moment";

export default {
  name: "CleanupPage",
  components: {
    NavigationBar
  },
  data() {
    return {
      // Loading states
      loading: false,
      isDeleting: false,
      searchTimeout: null,
      loadingSystemCount: false,

      // Pagination
      currentPage: 1,
      totalPages: 1,
      pageSize: '25',

      // Filters
      search: '',
      dateFrom: '',
      dateTo: '',

      // Data
      billDetails: [],
      selectedRecords: [],

      // Summary
      summary: { count: 0 },
      systemOrphanedCount: 0, // Total count in entire system

      // Deletion progress
      deletionProgress: 0,
      deletedCount: 0,
      totalToDelete: 0,
      deletionStatus: '',

      // Alerts
      alert: {
        success: false,
        info: false,
        failed: false
      },
      message: '',
      emptyStateMessage: 'All bills have corresponding transactions or no bills found matching your criteria.'
    }
  },
  computed: {
    actualPageSize() {
      return parseInt(this.pageSize);
    },

    visiblePages() {
      return this.pagination(this.currentPage, this.totalPages, 5);
    },

    paginationStart() {
      if (this.billDetails.length === 0) return 0;
      return ((this.currentPage - 1) * this.actualPageSize) + 1;
    },

    paginationEnd() {
      const end = this.currentPage * this.actualPageSize;
      return Math.min(end, this.summary.count);
    },

    isAllSelected() {
      return this.billDetails.length > 0 &&
          this.billDetails.every(item => this.selectedRecords.includes(item.billNo));
    }
  },
  mounted() {
    // Set default date range (last 3 months)
    this.dateTo = moment().format('YYYY-MM-DD');
    this.dateFrom = moment().subtract(3, 'months').format('YYYY-MM-DD');

    // Load initial data
    this.getSystemOrphanedCount();
    this.loadOrphanedRecords();
    this.menuAccess();
  },
  methods: {
    getRight,

    // Get total orphaned records count for entire system
    async getSystemOrphanedCount() {
      this.loadingSystemCount = true;

      try {
        const data = new FormData();
        data.append("function", "getOrphanedRecordsCount");

        const response = await execute(data, biller);

        if (response.data.success) {
          this.systemOrphanedCount = response.data.data.orphanedCount || 0;
        } else {
          console.error('Failed to get system orphaned count:', response.data.message);
        }
      } catch (error) {
        console.error('Error getting system orphaned count:', error);
      } finally {
        this.loadingSystemCount = false;
      }
    },

    // Refresh all data (system count + filtered records)
    async refreshAllData() {
      await Promise.all([
        this.getSystemOrphanedCount(),
        this.loadOrphanedRecords()
      ]);
    },

    // Main data loading method
    async loadOrphanedRecords() {
      this.loading = true;
      this.billDetails = [];
      this.selectedRecords = [];

      try {
        const data = new FormData();
        data.append("function", "getOrphanedBillDetails");
        data.append("page", this.currentPage);
        data.append("rows_per_page", this.actualPageSize);

        // Status filter - looking for PAID bills without transactions
        data.append("status", "PAID");

        // Date range
        if (this.dateFrom && this.dateTo) {
          data.append("dateFrom", this.dateFrom);
          data.append("dateTo", this.dateTo);
        }

        // Keyword search
        if (this.search) {
          data.append("keyword", this.search);
        }

        const response = await execute(data, biller);

        if (response.data.success) {
          this.billDetails = response.data.data.billDetails || [];
          this.summary = response.data.data.summary || { count: 0 };

          // Update total pages
          this.totalPages = Math.ceil(this.summary.count / this.actualPageSize);

          this.updateEmptyStateMessage();
        } else {
          this.showAlert('failed', response.data.message || 'Failed to load orphaned records');
        }
      } catch (error) {
        console.error('Error loading orphaned records:', error);
        this.showAlert('failed', 'An error occurred while loading orphaned records');
      } finally {
        this.loading = false;
      }
    },

    // Delete selected records with progress tracking
    async deleteSelected() {
      if (this.selectedRecords.length === 0) {
        this.showAlert('info', 'No records selected for deletion');
        return;
      }

      const confirmMessage = `⚠️ DATA CLEANUP CONFIRMATION

You are about to delete ${this.selectedRecords.length} orphaned bill records.

These are PAID bills that exist in the billdetails table but have NO corresponding entries in the transactions table.

This action will:
• Permanently remove these records from the database
• Cannot be undone
• Help clean up orphaned data

Are you sure you want to proceed?`;

      if (!confirm(confirmMessage)) {
        return;
      }

      this.isDeleting = true;
      this.deletionProgress = 0;
      this.deletedCount = 0;
      this.totalToDelete = this.selectedRecords.length;
      this.deletionStatus = 'Starting deletion process...';

      const recordsToDelete = [...this.selectedRecords];
      let successCount = 0;
      let errorCount = 0;

      for (let i = 0; i < recordsToDelete.length; i++) {
        const billNo = recordsToDelete[i];
        this.deletionStatus = `Deleting record ${i + 1} of ${recordsToDelete.length}: ${billNo}`;

        try {
          const success = await this.deleteRecord(billNo);
          if (success) {
            successCount++;
            // Remove from selected records
            this.selectedRecords = this.selectedRecords.filter(r => r !== billNo);
            // Remove from current display
            this.billDetails = this.billDetails.filter(item => item.billNo !== billNo);
          } else {
            errorCount++;
          }
        } catch (error) {
          console.error(`Error deleting ${billNo}:`, error);
          errorCount++;
        }

        this.deletedCount = i + 1;
        this.deletionProgress = ((i + 1) / recordsToDelete.length) * 100;

        // Small delay to show progress
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      this.deletionStatus = `Deletion complete: ${successCount} successful, ${errorCount} failed`;

      // Update summary count
      this.summary.count = Math.max(0, this.summary.count - successCount);

      // Update system count as well
      this.systemOrphanedCount = Math.max(0, this.systemOrphanedCount - successCount);

      // Show final result
      if (errorCount === 0) {
        this.showAlert('success', `Successfully deleted ${successCount} orphaned records. System total: ${this.numberFormat(this.systemOrphanedCount)}`);
      } else {
        this.showAlert('info', `Deleted ${successCount} records. ${errorCount} failed. System total: ${this.numberFormat(this.systemOrphanedCount)}`);
      }

      // Reset deletion state after a delay
      setTimeout(() => {
        this.isDeleting = false;
        this.deletionProgress = 0;
        this.deletedCount = 0;
        this.totalToDelete = 0;

        // Reload data if current page is empty
        if (this.billDetails.length === 0 && this.currentPage > 1) {
          this.currentPage = Math.max(1, this.currentPage - 1);
          this.loadOrphanedRecords();
        } else if (this.billDetails.length === 0) {
          this.loadOrphanedRecords();
        }
      }, 2000);
    },

    // Delete a single record
    async deleteSingleRecord(billNo) {
      if (!confirm(`⚠️ DELETE ORPHANED RECORD

Are you sure you want to delete this orphaned bill record?

Bill No: ${billNo}

This bill is marked as PAID but has no corresponding transaction record. Deleting it will permanently remove it from the database.

This action cannot be undone.`)) {
        return;
      }

      this.isDeleting = true;
      this.deletionStatus = `Deleting ${billNo}...`;

      try {
        const success = await this.deleteRecord(billNo);

        if (success) {
          // Remove from selected records if it was selected
          this.selectedRecords = this.selectedRecords.filter(r => r !== billNo);
          // Remove from current display
          this.billDetails = this.billDetails.filter(item => item.billNo !== billNo);
          // Update summary
          this.summary.count = Math.max(0, this.summary.count - 1);
          // Update system count
          this.systemOrphanedCount = Math.max(0, this.systemOrphanedCount - 1);

          this.showAlert('success', `Successfully deleted orphaned record ${billNo}. System total: ${this.numberFormat(this.systemOrphanedCount)}`);
        } else {
          this.showAlert('failed', `Failed to delete record ${billNo}`);
        }
      } catch (error) {
        console.error('Error deleting record:', error);
        this.showAlert('failed', 'An error occurred while deleting the record');
      } finally {
        this.isDeleting = false;

        // Reload data if current page is empty
        if (this.billDetails.length === 0 && this.currentPage > 1) {
          this.currentPage = Math.max(1, this.currentPage - 1);
          this.loadOrphanedRecords();
        } else if (this.billDetails.length === 0) {
          this.loadOrphanedRecords();
        }
      }
    },

    // API call to delete a single record
    async deleteRecord(billNo) {
      try {
        const data = new FormData();
        data.append("function", "deleteOrphanedBillDetail");
        data.append("billNo", billNo);

        const response = await execute(data, biller);
        return response.data.success;
      } catch (error) {
        console.error(`API error deleting ${billNo}:`, error);
        return false;
      }
    },

    // Selection methods
    toggleSelectAll() {
      if (this.isAllSelected) {
        // Deselect all current page items
        this.selectedRecords = this.selectedRecords.filter(billNo =>
            !this.billDetails.some(item => item.billNo === billNo)
        );
      } else {
        // Select all current page items
        this.billDetails.forEach(item => {
          if (!this.selectedRecords.includes(item.billNo)) {
            this.selectedRecords.push(item.billNo);
          }
        });
      }
    },

    clearSelection() {
      this.selectedRecords = [];
    },

    // Debounced search
    debounceSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.loadOrphanedRecords();
      }, 500);
    },

    // Filter handlers
    handleDateChange() {
      if (this.dateFrom && this.dateTo) {
        this.currentPage = 1;
        this.loadOrphanedRecords();
      }
    },

    handlePageSizeChange() {
      this.currentPage = 1;
      this.loadOrphanedRecords();
    },

    // Utility methods
    formatPrice(value) {
      const num = parseFloat(value) || 0;
      return new Intl.NumberFormat('en-KE', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 2
      }).format(num);
    },

    numberFormat(num) {
      return new Intl.NumberFormat("en-GB").format(num || 0);
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return moment(dateString).format('DD MMM YYYY');
    },

    maskPhoneNumber(input) {
      if (!input || typeof input !== 'string') return input;
      const digits = input.replace(/\D/g, '');
      if (digits.length > 4) {
        return input.slice(0, 2) + input.slice(2, -2).replace(/\d/g, '*') + input.slice(-2);
      }
      return input;
    },

    updateEmptyStateMessage() {
      let message = 'No orphaned records found';

      if (this.search) {
        message += ` matching "${this.search}"`;
      }
      if (this.dateFrom && this.dateTo) {
        message += ` between ${this.formatDate(this.dateFrom)} and ${this.formatDate(this.dateTo)}`;
      }

      this.emptyStateMessage = message + '. All bills have corresponding transactions.';
    },

    clearAlerts() {
      this.alert = { success: false, info: false, failed: false };
      this.message = '';
    },

    showAlert(type, message) {
      this.clearAlerts();
      this.alert[type] = true;
      this.message = message;

      setTimeout(() => {
        this.clearAlerts();
      }, 5000);
    },

    menuAccess() {
      const data = new FormData();
      data.append("function", "menuAccess");
      data.append("menu", "Data Cleanup");
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));

      execute(data, biller).catch(e => {
        console.error("Menu access error:", e.message);
      });
    },

    gotTo(route) {
      this.$router.push(route);
    },

    // Pagination methods
    pagination(currentPage, totalPages, maxVisibleButtons) {
      let pages = [];
      let start = currentPage - Math.floor(maxVisibleButtons / 2);
      let end = currentPage + Math.floor(maxVisibleButtons / 2);

      if (start < 1) {
        start = 1;
        end = Math.min(start + maxVisibleButtons - 1, totalPages);
      }

      if (end > totalPages) {
        end = totalPages;
        start = Math.max(end - maxVisibleButtons + 1, 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (start > 1) {
        pages.unshift("...");
        pages.unshift(1);
      }

      if (end < totalPages) {
        pages.push("...");
        pages.push(totalPages);
      }

      return pages;
    },

    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.loadOrphanedRecords();
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.loadOrphanedRecords();
      }
    },

    goToPage(page) {
      if (page !== '...' && page !== this.currentPage) {
        this.currentPage = page;
        this.loadOrphanedRecords();
      }
    }
  }
}
</script>

<style scoped>
.table th {
  white-space: nowrap;
  vertical-align: middle;
}

.table td {
  vertical-align: middle;
}

.form-label {
  font-weight: 600;
  font-size: 0.875rem;
}

.badge {
  font-size: 0.75rem;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
}

.card-header {
  padding: 1rem;
}

.toast-container {
  z-index: 9999;
}

.text-success {
  color: #198754 !important;
}

.text-primary {
  color: #0d6efd !important;
}

.bg-warning {
  background-color: #ffc107;
  color: #000;
}

.bg-info {
  background: linear-gradient(135deg, #17a2b8 0%, #0056b3 100%);
}

.pagination {
  margin-bottom: 0;
}

.page-link {
  cursor: pointer;
}

.page-item.disabled .page-link {
  cursor: not-allowed;
}

.progress {
  height: 1.5rem;
}

.progress-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

.btn-danger {
  background-color: #dc3545;
  border-color: #dc3545;
}

.btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.form-check-input:checked {
  background-color: #0d6efd;
  border-color: #0d6efd;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}
</style>